import type { AuditableEntity } from "@/features/types";

export type FaqStatus = "active" | "archived";

/**
 * "situation": something that happened to a caller, which they described rather
 * than asked about (locked out, a leak) — its answer is the procedure the agent
 * follows, and it is often recorded with none for the team to write.
 * Rows from before the field read as a question.
 */
export type FaqKind = "question" | "situation";

export interface Faq extends AuditableEntity {
  id: string;
  client_id: string;
  question: string;
  variations: string[];
  category: string;
  kind?: FaqKind | null;
  ai_response: string;
  refined_response: string;
  ask_count: number;
  last_asked_at: number;
  ai_response_updated_at: number;
  refined_response_updated_at: number;
  status: FaqStatus;
}

/** Whether anybody answered the FAQ yet — the team, else the AI. */
export function faqHasAnswer(faq: Faq): boolean {
  return !!(faq.refined_response?.trim() || faq.ai_response?.trim());
}

/**
 * A FAQ needs human refinement when the AI updated its answer more recently
 * than a human last reviewed it, or when it has no answer at all (a situation
 * the agent did not know how to handle). Mirrors the backend's
 * `listNeedingRefinement`.
 */
export function faqNeedsRefinement(faq: Faq): boolean {
  return (
    !faqHasAnswer(faq) ||
    (faq.ai_response_updated_at || 0) > (faq.refined_response_updated_at || 0)
  );
}
